import React from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { SecretarySettingRow } from './Stage3JuryPairs.types';

type Props = {
	busy: boolean;
	secretarySettings: SecretarySettingRow[];
	onSecretaryFieldChange: (
		districtId: number,
		field: 'title' | 'full_name' | 'phone' | 'secondary_email' | 'drive_folder_url',
		value: string,
	) => void;
	secretariesParentFolderUrl: string;
	setSecretariesParentFolderUrl: (value: string) => void;
	shareMessage: string;
	setShareMessage: (value: string) => void;
	testRecipientEmail: string;
	setTestRecipientEmail: (value: string) => void;
	sendNotificationEmail: boolean;
	setSendNotificationEmail: (value: boolean) => void;
	shareToSystemEmail: boolean;
	setShareToSystemEmail: (value: boolean) => void;
	shareToSecondaryEmail: boolean;
	setShareToSecondaryEmail: (value: boolean) => void;
	shareEnabled: boolean;
	overwriteExisting: boolean;
	setOverwriteExisting: (value: boolean) => void;
	onCreateSecretaryFolders: () => void;
	onVerifySecretaryFolders: () => void;
	onShareSecretaryFolders: () => void;
	onSaveSettings: () => void;
	secretariesActionReport: any | null;
	secretaryVerifyStatusByDistrict: Record<number, boolean>;
	secretaryVerifyErrorByDistrict: Record<number, string>;
};

const Stage3DriveSecretariesTab = ({
	busy,
	secretarySettings,
	onSecretaryFieldChange,
	secretariesParentFolderUrl,
	setSecretariesParentFolderUrl,
	shareMessage,
	setShareMessage,
	testRecipientEmail,
	setTestRecipientEmail,
	sendNotificationEmail,
	setSendNotificationEmail,
	shareToSystemEmail,
	setShareToSystemEmail,
	shareToSecondaryEmail,
	setShareToSecondaryEmail,
	shareEnabled,
	overwriteExisting,
	setOverwriteExisting,
	onCreateSecretaryFolders,
	onVerifySecretaryFolders,
	onShareSecretaryFolders,
	onSaveSettings,
	secretariesActionReport,
	secretaryVerifyStatusByDistrict,
	secretaryVerifyErrorByDistrict,
}: Props) => {
	return (
		<>
			<Typography variant='subtitle1'>Sekretarze i udostępnienia</Typography>
			<Alert
				severity='info'
				sx={{ mt: 1 }}
			>
				System korzysta z systemowych e-maili sekretarzy przypisanych do okręgów (konto WordPress + `district_id`).
			</Alert>
			<TextField
				fullWidth
				size='small'
				label='Folder nadrzędny dla okręgów (sekretarze)'
				value={secretariesParentFolderUrl}
				onChange={(e) => setSecretariesParentFolderUrl(e.target.value)}
				sx={{ mt: 1 }}
			/>
			<TextField
				fullWidth
				size='small'
				multiline
				minRows={2}
				label='Treść wiadomości udostępnienia (opcjonalnie)'
				value={shareMessage}
				onChange={(e) => setShareMessage(e.target.value)}
				sx={{ mt: 1 }}
			/>
			<TextField
				fullWidth
				size='small'
				label='Testowy e-mail override (opcjonalnie)'
				placeholder='np. test+drive@olijp.pl'
				value={testRecipientEmail}
				onChange={(e) => setTestRecipientEmail(e.target.value)}
				sx={{ mt: 1 }}
				helperText='Jeśli ustawione, wszystkie udostępnienia trafią wyłącznie na ten adres.'
			/>
			<Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
				<FormControlLabel
					control={
						<Checkbox
							checked={sendNotificationEmail}
							onChange={(_, checked) => setSendNotificationEmail(checked)}
						/>
					}
					label='Wyślij e-mail z powiadomieniem'
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={shareToSystemEmail}
							onChange={(_, checked) => setShareToSystemEmail(checked)}
						/>
					}
					label='Udostępnij na maile systemowe'
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={shareToSecondaryEmail}
							onChange={(_, checked) => setShareToSecondaryEmail(checked)}
						/>
					}
					label='Udostępnij na e-mail dodatkowy sekretarza'
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={overwriteExisting}
							onChange={(_, checked) => setOverwriteExisting(checked)}
						/>
					}
					label='Nadpisz istniejące linki folderów'
				/>
			</Box>
			<Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
				<Button
					variant='outlined'
					onClick={onSaveSettings}
					disabled={busy}
				>
					Zapisz ustawienia
				</Button>
				<Button
					variant='outlined'
					onClick={onCreateSecretaryFolders}
					disabled={busy}
				>
					Tworzenie
				</Button>
				<Button
					variant='outlined'
					onClick={onVerifySecretaryFolders}
					disabled={busy}
				>
					Weryfikacja
				</Button>
				<Button
					variant='contained'
					onClick={onShareSecretaryFolders}
					disabled={busy || !shareEnabled}
				>
					Udostępnianie
				</Button>
			</Box>
			{!shareEnabled && (
				<Typography
					variant='caption'
					color='warning.main'
				>
					Udostępnianie nieaktywne: zaznacz co najmniej jedną opcję maila lub ustaw testowy e-mail override.
				</Typography>
			)}

			<TableContainer
				component={Paper}
				sx={{ mt: 1, maxHeight: 300 }}
			>
				<Table
					size='small'
					stickyHeader
				>
					<TableHead>
						<TableRow>
							<TableCell>Okręg</TableCell>
							<TableCell>Email systemowy</TableCell>
							<TableCell>Email dodatkowy</TableCell>
							<TableCell>Folder</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{secretarySettings.map((row) => (
							<TableRow key={row.district_id}>
								<TableCell>{row.district_name}</TableCell>
								<TableCell>{row.system_email || '-'}</TableCell>
								<TableCell sx={{ minWidth: 180 }}>
									<TextField
										size='small'
										value={row.secondary_email}
										onChange={(e) => onSecretaryFieldChange(row.district_id, 'secondary_email', e.target.value)}
									/>
								</TableCell>
								<TableCell sx={{ minWidth: 260 }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
										<TextField
											size='small'
											value={row.drive_folder_url}
											onChange={(e) => onSecretaryFieldChange(row.district_id, 'drive_folder_url', e.target.value)}
										/>
										{secretaryVerifyStatusByDistrict[row.district_id] && (
											<Tooltip title='Folder zweryfikowany'>
												<CheckCircleIcon
													fontSize='small'
													color='success'
												/>
											</Tooltip>
										)}
									</Box>
									{!secretaryVerifyStatusByDistrict[row.district_id] &&
										secretaryVerifyErrorByDistrict[row.district_id] && (
											<Typography
												variant='caption'
												color='error.main'
											>
												{secretaryVerifyErrorByDistrict[row.district_id]}
											</Typography>
										)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			{secretariesActionReport && (
				<Box sx={{ mt: 1 }}>
					<details>
						<summary>Raport działań dla sekretarzy</summary>
						<TextField
							fullWidth
							multiline
							minRows={6}
							value={JSON.stringify(secretariesActionReport, null, 2)}
							InputProps={{ readOnly: true }}
							sx={{ mt: 1 }}
						/>
					</details>
				</Box>
			)}
		</>
	);
};

export default Stage3DriveSecretariesTab;
